import { OfferTypeEnum } from '../../services/exports/Constants';
import { Offer, SmartPricingRule } from '../../services/exports/Interfaces';

// Shared schema for the offer funnel events (offer_impression / offer_clicked /
// offer_applied). One builder per deal type keeps offer_id namespacing and the
// image_source derivation in a single, unit-testable place.

export enum OfferEventType {
  Bogo = 'bogo',
  PercentOff = 'percent_off',
  AmountOff = 'amount_off',
  FreeItem = 'free_item',
  MasterPromo = 'master_promo',
  SmartPricing = 'smart_pricing',
  WelcomeBonus = 'welcome_bonus',
  MobileSignup = 'mobile_signup',
}

export type OfferImageSource = 'ai_generated' | 'manual_upload' | 'none';

// sessionStorage key bridging the login bonus (received in AuthProvider, which sits
// OUTSIDE AnalyticsProvider) to the offer_applied conversion fired in AnalyticsProvider.
export const PENDING_BONUS_KEY = 'pending_bonus_conversion';

export interface DealEventProps {
  offer_id: string;
  // An integer, like the `restaurant_id` super property and Mixpanel group key: an event property
  // overrides the super property of the same name, so a string here split the group join.
  // Until the WLP work this was a string ("42"): partners' own Mixpanel projects hold string values
  // from before and integers after. Deliberate; keep it an integer.
  restaurant_id: number;
  offer_type: OfferEventType;
  has_image: boolean;
  image_source: OfferImageSource;
  guest_or_account: 'guest' | 'account';
  // Smart-pricing only context (omitted for every other type).
  modifier_type?: string;
  order_methods?: string[];
  is_global?: boolean;
}

export interface DealEventContext {
  restaurantId: string | number;
  isLoggedIn: boolean;
}

const OFFER_TYPE_MAP: Record<OfferTypeEnum, OfferEventType> = {
  [OfferTypeEnum.Bogo]: OfferEventType.Bogo,
  [OfferTypeEnum.PercentageOff]: OfferEventType.PercentOff,
  [OfferTypeEnum.AmountOff]: OfferEventType.AmountOff,
  [OfferTypeEnum.FreeItem]: OfferEventType.FreeItem,
};

const imageSourceFromUrl = (imageUrl?: string | null): OfferImageSource => (imageUrl ? 'manual_upload' : 'none');

const guestOrAccount = (isLoggedIn: boolean): DealEventProps['guest_or_account'] => (isLoggedIn ? 'account' : 'guest');

const base = (ctx: DealEventContext) => ({
  restaurant_id: Number(ctx.restaurantId),
  guest_or_account: guestOrAccount(ctx.isLoggedIn),
});

export const offerEventProps = (offer: Offer, ctx: DealEventContext): DealEventProps => ({
  ...base(ctx),
  offer_id: `offer:${offer.id}`,
  offer_type: OFFER_TYPE_MAP[offer.type],
  has_image: !!offer.image_url,
  // image_source comes from the backend (OfferResource); fall back to a URL-only
  // heuristic for resilience if the field is missing.
  image_source: offer.image_source ?? imageSourceFromUrl(offer.image_url),
});

export const masterPromoEventProps = (
  promo: { code: string; image_url?: string | null },
  ctx: DealEventContext,
): DealEventProps => ({
  ...base(ctx),
  offer_id: `promo:${promo.code}`,
  offer_type: OfferEventType.MasterPromo,
  has_image: !!promo.image_url,
  image_source: imageSourceFromUrl(promo.image_url),
});

export const smartPricingEventProps = (rule: SmartPricingRule, ctx: DealEventContext): DealEventProps => ({
  ...base(ctx),
  offer_id: `smart_pricing:${rule.id}`,
  offer_type: OfferEventType.SmartPricing,
  has_image: !!rule.image_url,
  image_source: imageSourceFromUrl(rule.image_url),
  modifier_type: rule.modifier_type,
  order_methods: rule.order_methods,
  is_global: rule.is_global,
});

export const bonusEventProps = (
  type: OfferEventType.WelcomeBonus | OfferEventType.MobileSignup,
  imageUrl: string | undefined | null,
  ctx: DealEventContext,
): DealEventProps => ({
  ...base(ctx),
  offer_id: type,
  offer_type: type,
  has_image: !!imageUrl,
  image_source: imageSourceFromUrl(imageUrl),
});
